import * as React from 'react';
import { styled } from '@linaria/react';

import { clamp } from '@/utils';
import useGrabSfx from '@/hooks/use-grab-sfx';
import useTickSfx from '@/hooks/use-tick-sfx';

import SvgWithGrid from '@/components/SvgWithGrid';

interface Point {
  x: number;
  y: number;
}

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  points: Array<Point>;
  setPoints: React.Dispatch<React.SetStateAction<Array<Point>>>;
  rx?: number;
  ry?: number;
  color: string;
  arcSize?: 'small' | 'large';
  renderedSize: number;
  viewboxSize: number;
  activePointIndex: number | null;
  setActivePointIndex: React.Dispatch<
    React.SetStateAction<number | null>
  >;
  handleReset: () => void;
}

function ArcGraph({
  points,
  setPoints,
  rx = 5,
  ry = 5,
  arcSize = 'small',
  color,
  renderedSize,
  viewboxSize,
  activePointIndex,
  setActivePointIndex,
  handleReset,
  ...delegated
}: Props) {
  const [playStartGrab, playReleaseGrab] = useGrabSfx();
  const playTick = useTickSfx();

  const pointsRef = React.useRef<Array<Point>>(points);
  pointsRef.current = points;

  const svgRef = React.useRef<SVGSVGElement>(null);

  const multiplier = renderedSize / viewboxSize;

  // The fallback input mechanism is to allow keyboard users to focus the control points and move them with arrow keys.
  // The trouble is that focusing the element also sets `activePointIndex`, which means that if the user keyboard-focuses the input and THEN moves the mouse, the point would jump to the cursor.
  // To prevent this, we track which input mechanism is currently active:
  // • on focus, we set it to "keyboard"
  // • on pointer down, we set it to "pointer"
  // • on blur and on pointer up, we set it to `null`
  const [inputMechanism, setInputMechanism] = React.useState<
    'keyboard' | 'pointer' | null
  >(null);

  React.useEffect(() => {
    if (activePointIndex === null || inputMechanism === 'keyboard') {
      return;
    }

    const handlePointerMove = (event: PointerEvent) => {
      const svg = svgRef.current;
      if (!svg) {
        return;
      }

      const rect = svg.getBoundingClientRect();
      let x = (event.clientX - rect.left) / multiplier;
      let y = (event.clientY - rect.top) / multiplier;

      x = Math.round(clamp(x, 0, viewboxSize + 0));
      y = Math.round(clamp(y, 0, viewboxSize + 0));

      const currentPosition = pointsRef.current[activePointIndex];
      const isChangingPosition =
        x !== currentPosition.x || y !== currentPosition.y;

      if (!isChangingPosition) {
        return;
      }

      playTick();

      setPoints(
        (currentValue: Array<Point>): Array<Point> =>
          currentValue.map((point, index) =>
            index === activePointIndex ? { x, y } : point
          )
      );
    };

    const handlePointerUp = () => {
      setActivePointIndex(null);
      document.body.style.cursor = 'default';
      setInputMechanism(null);
      playReleaseGrab();
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [
    inputMechanism,
    activePointIndex,
    multiplier,
    playReleaseGrab,
    playTick,
    setActivePointIndex,
    setPoints,
    viewboxSize,
  ]);

  const handleKeyPress = (key: string, index: number) => {
    let nudgeX = 0;
    let nudgeY = 0;

    if (key === 'ArrowLeft') {
      nudgeX = -1;
    } else if (key === 'ArrowRight') {
      nudgeX = 1;
    } else if (key === 'ArrowUp') {
      nudgeY = -1;
    } else if (key === 'ArrowDown') {
      nudgeY = 1;
    }

    setPoints(
      (currentValue: Array<Point>): Array<Point> =>
        currentValue.map((point, i) => {
          if (i !== index) {
            return point;
          }

          const { x, y } = point;

          let newX = x + nudgeX;
          let newY = y + nudgeY;

          newX = Math.round(clamp(newX, 0, viewboxSize));
          newY = Math.round(clamp(newY, 0, viewboxSize));

          return { x: newX, y: newY };
        })
    );
  };

  const handleStartDragging = (index: number) => {
    setInputMechanism('pointer');
    setActivePointIndex(index);
    document.body.style.cursor = 'grabbing';
    playStartGrab();
  };

  // Right now, the structure is always that we have a line -> arc -> line, with only the arc-adjacent points being draggable.
  // If I need alternative structures, I'll need to refactor this bit:
  const indices = [1, 2];

  return (
    <SvgWrapper
      style={{ width: renderedSize, height: renderedSize }}
      {...delegated}
    >
      <Svg
        ref={svgRef}
        gridGap={1}
        lineWidth={0.1}
        viewBox={`0 0 ${viewboxSize} ${viewboxSize}`}
        style={{ width: renderedSize, height: renderedSize }}
      >
        <Shape
          stroke={color}
          d={`
            M ${points[0].x} ${points[0].y}
            L ${points[1].x} ${points[1].y}
            A ${rx} ${ry} 0 ${arcSize === 'small' ? 0 : 1} ${
              arcSize === 'small' ? 0 : 1
            }  ${points[2].x} ${points[2].y}
            L ${points[3].x} ${points[3].y}
          `}
        />
      </Svg>
      {indices.map((index) => (
        <Handle
          key={index}
          onPointerDown={() => {
            handleStartDragging(index);
          }}
          onFocus={() => {
            if (inputMechanism === 'pointer') {
              return;
            }

            setActivePointIndex(index);
            setInputMechanism('keyboard');
          }}
          onBlur={() => {
            if (inputMechanism === 'keyboard') {
              setActivePointIndex(null);
              setInputMechanism(null);
            }
          }}
          onKeyDown={(event) => {
            if (event.key === 'Delete') {
              handleReset();
            } else if (event.key.includes('Arrow')) {
              event.preventDefault();
              handleKeyPress(event.key, index);
            }
          }}
          style={{
            top: points[index].y * multiplier,
            left: points[index].x * multiplier,
          }}
        >
          <PointLabel draggable={false}>
            ({points[index].x}, {points[index].y})
          </PointLabel>
        </Handle>
      ))}
    </SvgWrapper>
  );
}

const SvgWrapper = styled.div`
  align-self: flex-start;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Svg = styled(SvgWithGrid)`
  display: block;
  outline: 1px solid var(--color-gray-300);
  border-radius: 4px;
`;

const Shape = styled.path`
  fill: none;
  stroke-width: 0.5px;
`;

const Handle = styled.button`
  position: absolute;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: var(--color-text);
  transform: translate(-50%, -50%);
  border: 4px solid var(--color-background);
  cursor: grab;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }
`;

const PointLabel = styled.div`
  position: absolute;
  top: -8px;
  left: 50%;
  white-space: nowrap;
  transform: translate(-50%, -100%);
  background: var(--color-background);
  user-select: none;
  font-size: 0.75rem;
  font-family: var(--font-family-mono);
  font-weight: 500;
  color: white;
  padding: 2px 4px;
  border-radius: 4px;
  pointer-events: none;
  opacity: 0;
  transition:
    opacity 500ms,
    color 200ms;

  ${Handle}:active &,
  ${Handle}:focus-visible & {
    color: var(--wham-primary-500);
    transition:
      opacity 100ms,
      color 100ms;
    opacity: 1;
  }
`;

export default ArcGraph;
